import React, {  useEffect, useRef, useState } from 'react';
import * as ReactDOMServer from 'react-dom/server';
import { ICellRendererParams } from 'ag-grid-community';
import { useDataTableContext } from '../shared/Context';
import PortalTooltip from './TooltipPortal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { faCopy as faCopySolid  } from '@fortawesome/pro-solid-svg-icons';
import { ColumnType } from '../../DataTable/DataTypes';

interface CustomCellRendererProps extends ICellRendererParams {
  col?: ColumnType,
  enableMultilineCellRender?: boolean,
  hasRowHeightOption?: boolean;
}

const CustomCellRenderer: React.FC<CustomCellRendererProps> = (props) => {
  const { columnWidthsRef, maxContentHeight, setMaxContentHeight } = useDataTableContext();
  const spanRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const enableMultilineCellRender : boolean = props.enableMultilineCellRender ?? false;
  const [isVerticallyOverflowing, setIsVerticallyOverflowing] = useState(false);
  const [isHorizontallyOverflowing, setIsHorizontallyOverflowing] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [copied, setCopied] = useState(false);
  const hasRowHeightOption = props.hasRowHeightOption || false;
  const currentWidth = columnWidthsRef.current.get(props.colDef.field ?? '');

  const rowData = props.data;
  const col = props.col;
  const alignment = col?.dataAlign || 'left';
  const showDefaultTooltip = !(col?.hideDefaultTooltip ?? false);

 
  useEffect(() => {
    const el = spanRef.current;
    const container = containerRef.current;
    if (el && container && props.node) {
      const horizontal = el.scrollWidth > el.clientWidth;
      const vertical = el.scrollHeight - 2 > el.clientHeight;
      setIsHorizontallyOverflowing(horizontal);
      setIsVerticallyOverflowing(vertical);
      // Calculate actual content height
      let contentHeight = el.scrollHeight;

      // Update maxContentHeight in context if this height is larger
      if (contentHeight > maxContentHeight) {
        setMaxContentHeight(contentHeight);
      }
    }
  }, [props.value, currentWidth, props.node]);

  const renderedValue: string | undefined = (() => {
    if (col?.render && rowData) {
      const val = col.render(rowData);
      return typeof val === 'string' ? val : val?.toString();
    }
    return typeof props.value === 'string' ? props.value : props.value?.toString();
  })();

    const handleCopy = (e: React.MouseEvent) => {
      e.stopPropagation();

      let copyText = "";

      if (col?.copyValue && rowData) {
        // If copyValue is defined, use it directly
        const val = col.copyValue(rowData);
        if (React.isValidElement(val)) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = ReactDOMServer.renderToStaticMarkup(val);

          // Process the HTML to preserve formatting
          const processNode = (node: Node): string => {
            if (node.nodeType === Node.TEXT_NODE) {
              return node.textContent || '';
            }

            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              const tagName = element.tagName.toLowerCase();

              // Preserve line breaks and spacing
              if (tagName === 'br' || tagName === 'p' || tagName === 'div') {
                return '\n' + Array.from(element.childNodes).map(processNode).join('');
              }

              // Handle progress bars and other special elements
              if (element.className?.includes('progress')) {
                const progressText = element.textContent?.trim();
                return progressText ? `[${progressText}]` : '';
              }

              // For other elements, just process their children
              return Array.from(element.childNodes).map(processNode).join('');
            }

            return '';
          };

          copyText = processNode(tempDiv)
            .split('\n')
            .map(line => line.trim())
            .filter(line => line) // Remove empty lines
            .join('\n')
            .replace(/\n{3,}/g, '\n\n'); // Limit consecutive newlines to 2
        } else if (typeof val === 'string') {
          copyText = val.replace(/<br\s*\/?>/gi, '\n') // Convert <br> to newlines
            .replace(/<p.*?>/gi, '\n') // Convert <p> to newlines
            .replace(/<[^>]+>/g, '') // Remove other HTML tags
            .replace(/\n{3,}/g, '\n\n') // Limit consecutive newlines to 2
            .trim();
        } else if (typeof val === 'object') {
          copyText = JSON.stringify(val, null, 2);
        } else {
          copyText = String(val);
        }
      }
      else if (col?.render && rowData) {
        const val = col.render(rowData);
        if (typeof val === "string") {
          copyText = val.replace(/<[^>]+>/g, "");
        } else if (typeof val === "object") {
          if (React.isValidElement(val)) {
            const originalData = col.key && rowData[col.key];
            if (originalData && typeof originalData === 'object') {
              copyText = Object.entries(originalData)
                .filter(([_, value]) => value != null)
                .map(([key, value]) => {
                  const valueStr = typeof value === 'object' 
                    ? JSON.stringify(value, null, 2)
                    : value.toString();
                  return `${key} = ${valueStr}`;
                })
                .join('\n');
            } else {
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = ReactDOMServer.renderToString(val);
              copyText = tempDiv.textContent || tempDiv.innerText || "";
            }
          } else {
            copyText = JSON.stringify(val, null, 2);
          }
        }
      } 
      else if (col?.htmlRender && rowData) {
        copyText = col.htmlRender(rowData).replace(/<[^>]+>/g, "");
      } 
      else {
        copyText = props.value?.toString() ?? "";
      }

      if (copyText) {
        navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    }
  };

  return (
    <div
      ref={containerRef}
        style={{
        position: 'relative',
        textAlign: alignment,
        width: '100%',
        height: '100%',
        paddingTop: hasRowHeightOption ? "4px" : undefined,
        paddingRight: col?.showCopyLink ? '20px' : '0', 
      }}
      onMouseEnter={() => {
        setHovering(true);
        if (isHorizontallyOverflowing || isVerticallyOverflowing) {
          setShowTooltip(true);
        }
      }}
      onMouseLeave={() => {
        setHovering(false);
        setShowTooltip(false);
      }}
      onClick={(e) => {
        e.stopPropagation();
        
      }}
    >
  {
    (col.render || col.htmlRender) ? (
      <div style={{
        display: 'flex',
        alignItems: enableMultilineCellRender ? undefined : hasRowHeightOption ? undefined : 'center',    
        justifyContent: alignment === 'right' ? 'flex-end' : alignment === 'center' ? 'center' : 'flex-start',
        height: '100%',
      }}>
        {col.render ? (
        <div style={{ position: 'relative', width: '100%' }}>
        <span
          ref={spanRef}
          style={{
            display: enableMultilineCellRender ? '-webkit-box' : 'block',
            flexGrow: enableMultilineCellRender?undefined : 1,
            WebkitBoxOrient: enableMultilineCellRender ? 'vertical' : undefined,
            WebkitLineClamp: enableMultilineCellRender ? 2 : undefined,
            textOverflow: 'ellipsis',
            lineHeight: '1.4',
            height: '100%',
            overflow: 'hidden',
            wordBreak: enableMultilineCellRender ? 'break-word' : undefined,
            whiteSpace: enableMultilineCellRender ? 'normal' : undefined,
          }}
        >
            {rowData ? col.render?.(rowData) : null}          
        </span>
          {isVerticallyOverflowing && enableMultilineCellRender && (
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                height: '3.5em',
                width: '100%',
                background: 'linear-gradient(to bottom, transparent, white)', 
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
        ):(
          <span
            ref={spanRef}
            dangerouslySetInnerHTML={{ __html: col.htmlRender?.(rowData) || '' }}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              flexGrow: 1,
              lineHeight: '1.4',
              width: '100%',
              textAlign: alignment ?? 'left',
            }}
          />
        )}
      </div>
   ) : (
      <span
        ref={spanRef}
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: hasRowHeightOption ? 'flex' : 'block',
          flexGrow: 1,
          justifyContent: hasRowHeightOption ? "center" : undefined,
          alignItems: hasRowHeightOption ? "center" : undefined,
        }}
      >
      {col.render_icon ? (
        (() => {
          const icon : IconDefinition = col.render_icon(rowData);
          return icon ? <FontAwesomeIcon icon={icon} /> : undefined;
        })()
      ) : (
        hasRowHeightOption ? 
        (<span 
        style={{ 
            lineHeight: '1.4',
            wordBreak: 'break-word',
            whiteSpace: 'normal',  
        }}>
          {props.value}
        </span>) :
        <span>{props.value}</span>
      )}
            </span>
          )
        }

      {col?.showCopyLink && hovering &&  renderedValue &&(
      <span
        style={{
          position: 'absolute',              
          top: hasRowHeightOption ? '15px' : '50%',
          right: '14px',
          transform: 'translateY(-50%)',
          cursor: 'pointer',
          zIndex: 1,
          backgroundColor: 'transparent', 
        }}
        className="no-click-propagation" 
        onClick={(e) => {
          e.stopPropagation();
          handleCopy(e);      
        }}
      >
        <FontAwesomeIcon
          icon={copied ? faCopySolid : faCopy}
          style={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',  
            cursor: 'pointer',
            color: copied ? 'black' : '#888',
            transition: 'color 0.4s',
          }}
          title={copied ? 'Copied!' : 'Copy'}
        />
      </span>
      )}

      {showDefaultTooltip &&
        <PortalTooltip isOpen={showTooltip} targetRef={spanRef}>
          {col?.render_tooltip ? col.render_tooltip(rowData) : col?.render?.(rowData) ?? String(props.value)
          }
        </PortalTooltip>
      }
    </div>
  );
};

export default CustomCellRenderer;


